import { useCallback, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";
import { flotteFiltersAtom } from "./useFlotte";
import {
  columnsAtom,
  activeGroupsAtom,
  stickyIdentiteAtom,
} from "../components/FlotteTable/useFlotteTable";
import { columnsConfig } from "../components/FlotteTable/flotteColumns.config";
import specialFiltersConfig from "../components/FlotteTable/specialFilters.config";

const STORAGE_KEY_FILTERS = "flotte_filters";
const STORAGE_KEY_COLUMNS = "flotte_columns";
const STORAGE_KEY_GROUPS = "flotte_active_groups";
const STORAGE_KEY_STICKY = "flotte_sticky_identite";

// Clés de filtres qui sont des arrays
const ARRAY_FILTER_KEYS = new Set();
Object.values(columnsConfig).forEach((col) => {
  if (col.filterable && col.filterable.filterType) {
    if (
      col.filterable.filterType === "tag-input" ||
      col.filterable.filterType === "select-multiple"
    ) {
      ARRAY_FILTER_KEYS.add(col.filterable.filterKey);
    }
  }
});

// Clés de filtres de type date-range
const DATE_RANGE_FILTER_KEYS = new Set();
Object.values(columnsConfig).forEach((col) => {
  if (col.filterable && col.filterable.filterType === "date-range") {
    DATE_RANGE_FILTER_KEYS.add(col.filterable.filterKey);
  }
});

// Toutes les filterKeys valides (colonnes + spéciaux)
const VALID_FILTER_KEYS = new Set();
Object.values(columnsConfig).forEach((col) => {
  if (col.filterable && col.filterable.filterKey) {
    VALID_FILTER_KEYS.add(col.filterable.filterKey);
  }
});
specialFiltersConfig.forEach((f) => {
  VALID_FILTER_KEYS.add(f.filterKey);
});

/**
 * Sérialise les filtres en paramètres URL plats.
 * - Arrays -> clé[]=val1&clé[]=val2 (géré par query-string arrayFormat bracket)
 * - Date ranges -> clé_from=...&clé_to=...
 * - Booleans -> clé=1
 * - Strings -> clé=val
 */
const filtersToUrlParams = (filters) => {
  const params = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (key === "page") return;
    if (value === null || value === undefined || value === "") return;

    if (
      DATE_RANGE_FILTER_KEYS.has(key) &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      if (value.from) params[`${key}_from`] = value.from;
      if (value.to) params[`${key}_to`] = value.to;
    } else if (Array.isArray(value)) {
      if (value.length > 0) params[key] = value;
    } else if (typeof value === "boolean") {
      if (value) params[key] = "1";
    } else {
      params[key] = value;
    }
  });
  return params;
};

/**
 * Parse les paramètres URL vers un objet filtres.
 */
const urlParamsToFilters = (search) => {
  const raw = queryString.parse(search, { arrayFormat: "bracket" });
  const filters = {};

  // Regrouper les date-range (_from, _to)
  const dateRangeAccum = {};

  Object.entries(raw).forEach(([key, value]) => {
    if (key === "page") return;
    // Ignorer les paramètres de colonnes, de groupes et de colonne figée
    if (key === "cols" || key === "groups" || key === "sticky") return;

    // Gérer les suffixes date-range
    const fromMatch = key.match(/^(.+)_from$/);
    const toMatch = key.match(/^(.+)_to$/);

    if (fromMatch && DATE_RANGE_FILTER_KEYS.has(fromMatch[1])) {
      const baseKey = fromMatch[1];
      if (!dateRangeAccum[baseKey]) dateRangeAccum[baseKey] = {};
      dateRangeAccum[baseKey].from = value;
      return;
    }
    if (toMatch && DATE_RANGE_FILTER_KEYS.has(toMatch[1])) {
      const baseKey = toMatch[1];
      if (!dateRangeAccum[baseKey]) dateRangeAccum[baseKey] = {};
      dateRangeAccum[baseKey].to = value;
      return;
    }

    if (!VALID_FILTER_KEYS.has(key)) return;

    // Convertir les arrays
    if (ARRAY_FILTER_KEYS.has(key)) {
      filters[key] = Array.isArray(value) ? value : value ? [value] : [];
    } else {
      // Filtres spéciaux (booleans)
      const specialFilter = specialFiltersConfig.find(
        (f) => f.filterKey === key,
      );
      if (specialFilter) {
        filters[key] =
          value === "1" || value === "true" ? specialFilter.value : undefined;
      } else {
        filters[key] = value;
      }
    }
  });

  // Ajouter les date-ranges
  Object.entries(dateRangeAccum).forEach(([key, range]) => {
    filters[key] = range;
  });

  // Supprimer les undefined
  Object.keys(filters).forEach((key) => {
    if (filters[key] === undefined) delete filters[key];
  });

  return filters;
};

/**
 * Sérialise la visibilité des colonnes en un paramètre URL compact.
 * On stocke uniquement les colonnes dont la visibilité diffère du défaut.
 * Format: cols=+key1,-key2,+key3 (+ = visible, - = masqué)
 */
const columnsToUrlParam = (columns) => {
  const diffs = [];
  Object.entries(columns).forEach(([key, col]) => {
    const defaultVisible = columnsConfig[key]?.visible ?? false;
    if (col.visible !== defaultVisible) {
      diffs.push(`${col.visible ? "+" : "-"}${key}`);
    }
  });
  return diffs.length > 0 ? diffs.join(",") : null;
};

/**
 * Parse le paramètre URL cols vers un objet {key: boolean}
 */
const urlParamToColumns = (colsParam) => {
  if (!colsParam) return null;
  const result = {};
  colsParam.split(",").forEach((entry) => {
    if (entry.length < 2) return;
    const prefix = entry[0];
    const key = entry.substring(1);
    if (columnsConfig[key] !== undefined) {
      result[key] = prefix === "+";
    }
  });
  return Object.keys(result).length > 0 ? result : null;
};

/**
 * Sérialise les groupes actifs en paramètre URL compact.
 * Format: groups=contrat,bb
 */
const groupsToUrlParam = (groups) =>
  Array.isArray(groups) && groups.length > 0 ? groups.join(",") : null;

/**
 * Parse le paramètre URL groups vers un tableau de groupes actifs
 */
const urlParamToGroups = (groupsParam) => {
  if (!groupsParam) return null;
  const groups = groupsParam.split(",").filter(Boolean);
  return groups.length > 0 ? groups : null;
};

/**
 * Sauvegarde le choix « colonne de synthèse figée » dans le localStorage.
 * Comme pour les colonnes, on ne stocke que l'écart au défaut (false).
 */
const saveStickyToStorage = (sticky) => {
  try {
    if (sticky) {
      localStorage.setItem(STORAGE_KEY_STICKY, "1");
    } else {
      localStorage.removeItem(STORAGE_KEY_STICKY);
    }
  } catch (e) {
    // Silently fail
  }
};

/**
 * Charge le choix « colonne de synthèse figée » depuis le localStorage
 */
const loadStickyFromStorage = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_STICKY) === "1";
  } catch (e) {
    return false;
  }
};

/**
 * Sauvegarde les filtres dans le localStorage
 */
const saveFiltersToStorage = (filters) => {
  try {
    const clean = { ...filters };
    delete clean.page;
    localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(clean));
  } catch (e) {
    // Silently fail
  }
};

/**
 * Charge les filtres depuis le localStorage
 */
const loadFiltersFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FILTERS);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Sauvegarde la visibilité des colonnes dans le localStorage.
 * On stocke uniquement les différences par rapport au défaut.
 */
const saveColumnsToStorage = (columns) => {
  try {
    const diffs = {};
    Object.entries(columns).forEach(([key, col]) => {
      const defaultVisible = columnsConfig[key]?.visible ?? false;
      if (col.visible !== defaultVisible) {
        diffs[key] = col.visible;
      }
    });
    localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(diffs));
  } catch (e) {
    // Silently fail
  }
};

/**
 * Charge la visibilité des colonnes depuis le localStorage.
 * Retourne un objet {key: boolean} des colonnes modifiées, ou null.
 */
const loadColumnsFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_COLUMNS);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return Object.keys(parsed).length > 0 ? parsed : null;
  } catch (e) {
    return null;
  }
};

/**
 * Sauvegarde les groupes actifs dans le localStorage
 */
const saveGroupsToStorage = (groups) => {
  try {
    if (Array.isArray(groups) && groups.length > 0) {
      localStorage.setItem(STORAGE_KEY_GROUPS, JSON.stringify(groups));
    } else {
      localStorage.removeItem(STORAGE_KEY_GROUPS);
    }
  } catch (e) {
    // Silently fail
  }
};

/**
 * Charge les groupes actifs depuis le localStorage
 */
const loadGroupsFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_GROUPS);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch (e) {
    return null;
  }
};

/**
 * Vérifie si l'URL contient des paramètres de flotte (filtres ou colonnes)
 */
const hasUrlParams = (search) => {
  const raw = queryString.parse(search, { arrayFormat: "bracket" });
  return Object.keys(raw).length > 0;
};

/**
 * Hook de synchronisation URL <-> localStorage <-> atoms pour la flotte.
 *
 * Priorité à l'initialisation :
 * 1. Si l'URL contient des paramètres → utiliser l'URL, mettre à jour le localStorage
 * 2. Sinon si le localStorage contient des données → utiliser le localStorage, mettre à jour l'URL
 * 3. Sinon → défauts (pas de filtres, visibilité par défaut)
 *
 * En temps réel :
 * - Toute modification de filtre ou colonne met à jour l'URL et le localStorage
 */
const useFlotteUrlSync = () => {
  const history = useHistory();
  const location = useLocation();
  const [flotteFilters, setFlotteFilters] = useAtom(flotteFiltersAtom);
  const [columns, setColumns] = useAtom(columnsAtom);
  const [activeGroups, setActiveGroups] = useAtom(activeGroupsAtom);
  const [stickyIdentite, setStickyIdentite] = useAtom(stickyIdentiteAtom);
  const isInitialized = useRef(false);
  const skipNextUrlUpdate = useRef(false);

  /**
   * Met à jour l'URL avec les filtres, colonnes et groupes actifs actuels
   */
  const updateUrl = useCallback(
    (filters, cols, groups, sticky) => {
      const filterParams = filtersToUrlParams(filters);
      const colsParam = columnsToUrlParam(cols);
      if (colsParam) {
        filterParams.cols = colsParam;
      }
      const groupsParam = groupsToUrlParam(groups);
      if (groupsParam) {
        filterParams.groups = groupsParam;
      }
      if (sticky) {
        filterParams.sticky = "1";
      }

      const newSearch = queryString.stringify(filterParams, {
        arrayFormat: "bracket",
      });
      const currentSearch = location.search.replace(/^\?/, "");

      if (newSearch !== currentSearch) {
        history.replace(newSearch ? `?${newSearch}` : location.pathname);
      }
    },
    [history, location.pathname, location.search],
  );

  /**
   * Initialisation : déterminer la source de vérité (URL vs localStorage)
   * Retourne les filtres, colonnes et groupes actifs initiaux
   */
  const initialize = useCallback(() => {
    if (isInitialized.current) return null;
    isInitialized.current = true;

    const urlHasParams = hasUrlParams(location.search);

    if (urlHasParams) {
      // Source = URL → mettre à jour localStorage
      const urlFilters = urlParamsToFilters(location.search);
      const raw = queryString.parse(location.search, {
        arrayFormat: "bracket",
      });
      const urlColumns = urlParamToColumns(raw.cols);
      const urlGroups = urlParamToGroups(raw.groups);

      // Appliquer les filtres depuis l'URL
      if (Object.keys(urlFilters).length > 0) {
        setFlotteFilters(urlFilters);
        saveFiltersToStorage(urlFilters);
      }

      // Appliquer les colonnes depuis l'URL
      if (urlColumns) {
        setColumns((prev) => {
          const updated = { ...prev };
          Object.entries(urlColumns).forEach(([key, visible]) => {
            if (updated[key]) {
              updated[key] = { ...updated[key], visible };
            }
          });
          return updated;
        });
        // Le save du localStorage se fera au prochain render via l'effet de sync
      }

      // Appliquer les groupes actifs depuis l'URL
      if (urlGroups) {
        setActiveGroups(urlGroups);
      }

      // Appliquer la colonne de synthèse figée depuis l'URL
      const urlSticky = raw.sticky === "1" || raw.sticky === "true";
      if (urlSticky) {
        setStickyIdentite(true);
      }

      return {
        filters: urlFilters,
        columns: urlColumns,
        groups: urlGroups,
        sticky: urlSticky,
      };
    }

    // Source = localStorage
    const storedFilters = loadFiltersFromStorage();
    const storedColumns = loadColumnsFromStorage();
    const storedGroups = loadGroupsFromStorage();
    const storedSticky = loadStickyFromStorage();

    if (storedFilters && Object.keys(storedFilters).length > 0) {
      setFlotteFilters(storedFilters);
    }

    if (storedColumns) {
      setColumns((prev) => {
        const updated = { ...prev };
        Object.entries(storedColumns).forEach(([key, visible]) => {
          if (updated[key]) {
            updated[key] = { ...updated[key], visible };
          }
        });
        return updated;
      });
    }

    if (storedGroups) {
      setActiveGroups(storedGroups);
    }

    if (storedSticky) {
      setStickyIdentite(true);
    }

    // Mettre à jour l'URL depuis le localStorage
    skipNextUrlUpdate.current = true;

    return {
      filters: storedFilters || {},
      columns: storedColumns || null,
      groups: storedGroups || null,
      sticky: storedSticky,
    };
  }, [
    location.search,
    setFlotteFilters,
    setColumns,
    setActiveGroups,
    setStickyIdentite,
  ]);

  /**
   * Effet de synchronisation : quand les filtres, colonnes ou groupes actifs
   * changent, mettre à jour l'URL et le localStorage.
   */
  useEffect(() => {
    if (!isInitialized.current) return;
    if (skipNextUrlUpdate.current) {
      skipNextUrlUpdate.current = false;
      // Quand même mettre à jour l'URL depuis le localStorage
      updateUrl(flotteFilters, columns, activeGroups, stickyIdentite);
      return;
    }

    saveFiltersToStorage(flotteFilters);
    saveColumnsToStorage(columns);
    saveGroupsToStorage(activeGroups);
    saveStickyToStorage(stickyIdentite);
    updateUrl(flotteFilters, columns, activeGroups, stickyIdentite);
  }, [flotteFilters, columns, activeGroups, stickyIdentite, updateUrl]);

  return { initialize };
};

export default useFlotteUrlSync;
